import { PicklistEventType } from '@warebee/shared/engine-model';
import { QueryCreator, sql } from 'kysely';
import { DatasetDatabase } from '../datasetQueryBuilder';
import { DocumentBaseBaseQueryBuilderParams } from '../datasetQueryBuilder.types';
import { getAnalyzedJobEventQueryBuilder } from './analyzedJobEventQueryBuilder';

export function getAnalyzedJobLineQueryBuilder<T extends DatasetDatabase>(
  params: DocumentBaseBaseQueryBuilderParams,
  db: QueryCreator<T>,
) {
  const builder = db
    .with(
      cte => cte('aje'),
      db => getAnalyzedJobEventQueryBuilder(params, db).selectAll(),
    )
    .with(
      cte => cte('ajl'),
      db => {
        return db
          .selectFrom('aje')
          .select(({ fn }) => [
            'sourceEventId',
            'sourceEventDate',
            fn.sum<number>('cost').as('cost'),
            fn.sum<number>('duration').as('duration'),

            fn
              .sum<number>(sql`abs(${sql.ref('distance')})`)
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_START,
                PicklistEventType.TRAVELLING_HORIZONTAL_END,
                PicklistEventType.TRAVELLING_HORIZONTAL,
              ] as any)
              .as('distance'),

            // #region TRAVELLING START
            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_START,
              ] as any)
              .as('durationTravellingStart'),
            fn
              .sum<number>('distance')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_START,
              ] as any)
              .as('distanceTravellingStart'),

            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_START,
              ] as any)
              .as('costTravellingStart'),
            //#endregion

            // #region TRAVELLING END
            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_END,
              ] as any)
              .as('durationTravellingEnd'),
            fn
              .sum<number>('distance')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_END,
              ] as any)
              .as('distanceTravellingEnd'),
            // #endregion
            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL_END,
              ] as any)
              .as('costTravellingEnd'),
            //#endregion

            // #region TRAVELLING INTERNAL
            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL,
              ] as any)
              .as('durationTravellingInternal'),
            fn
              .sum<number>('distance')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL,
              ] as any)
              .as('distanceTravellingInternal'),
            // #endregion
            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', [
                PicklistEventType.TRAVELLING_HORIZONTAL,
              ] as any)
              .as('costTravellingInternal'),
            //#endregion

            // #region TRAVELLING VERTICAL
            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', ['TRAVELLING_VERTICAL'] as any)
              .as('durationTravellingVertical'),
            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', ['TRAVELLING_VERTICAL'] as any)
              .as('costTravellingVertical'),
            fn
              .sum<number>('distance')
              .filterWhere('eventType', 'in', ['TRAVELLING_VERTICAL'] as any)
              .as('distanceTravellingVertical'),
            //#endregion

            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', [
                'PRE_HANDLING',
                'HANDLING_PREPARATION',
                'HANDLING_ON_COMPLETION',
                'POST_HANDLING',
              ] as any)
              .as('durationHandling'),

            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', [
                'PRE_HANDLING',
                'HANDLING_PREPARATION',
                'HANDLING_ON_COMPLETION',
                'POST_HANDLING',
              ] as any)
              .as('costHandling'),

            //PICKING
            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', ['HANDLING_EXECUTION'] as any)
              .as('durationPicking'),
            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', ['HANDLING_EXECUTION'] as any)
              .as('costPicking'),

            //REORDERING
            fn
              .sum<number>('duration')
              .filterWhere('eventType', 'in', [
                'HANDLING_PALLET_REORDERING',
              ] as any)
              .as('durationReordering'),
            fn
              .sum<number>('cost')
              .filterWhere('eventType', 'in', [
                'HANDLING_PALLET_REORDERING',
              ] as any)
              .as('costReordering'),

            fn
              .agg<string[]>('array_agg', [
                sql<string>`	
                array_union(
                  array_union(
                    array [ json_parse(start_position) ],
                    coalesce(
                      cast(
                        json_parse(
                          json_query(
                            details,
                            'strict $.route[*].position' with array wrapper
                          )
                        ) as array(json)
                      ),
                      ARRAY [ ]
                    )
                  ),
                  array [ json_parse(end_position) ]
                )
                `,
                // sql<string>`json_query(${sql.ref('details')}, 'strict $.route')`,
              ])
              .filterWhere('eventType', 'in', [
                'TRAVELLING_HORIZONTAL_START',
                'TRAVELLING_HORIZONTAL',
                'TRAVELLING_HORIZONTAL_END',
              ] as any)
              .as('route'),
            fn
              .agg<string[]>('array_agg', [
                sql<string>`json_value(${sql.ref('details')}, 'strict $.reason')`,
              ])
              .filterWhere('eventType', '=', 'SOURCE_EVENT_IGNORED' as any)
              .as('reasons'),
            fn
              .countAll<number>()
              .filterWhere('eventType', '=', 'SOURCE_EVENT_IGNORED' as any)
              .as('ignoredEventsCount'),
          ])
          .groupBy(['sourceEventId', 'sourceEventDate']);
      },
    );
  return builder;
}
